import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { createTheme, ThemeProvider, type ThemeOptions } from '@mui/material/styles'
import {
  IconCalendar,
  IconChevronRight,
  IconInbox,
  IconNews,
  IconSpeakerphone,
} from '@tabler/icons-react'
import baseTheme, { tokens } from '@/theme'
import { apiUrl } from '@/lib/apiBase'
import NewsletterSignup from '@/components/NewsletterSignup'
import NotFound from '@/pages/NotFound'

// ─── Contract types — must mirror the T-203 backend shapes exactly ─────────────

interface Basin {
  name: string | null
  priority_level: string | null
  is_critically_overdrafted: boolean | null
}

interface PilotGsa {
  gsa_id: number
  name: string
  county: string | null
  basin: Basin | null
}

interface TenantPage {
  slug: string
  display_name: string | null
  logo_url: string | null
  primary_color: string | null
  gsa: PilotGsa
}

interface Meeting {
  meeting_id: string
  meeting_date: string | null
  meeting_type: string | null
  // GSA-authored document links (ag-009) — published directly by the GSA.
  agenda_url?: string | null
  minutes_url?: string | null
  video_url?: string | null
  summary_title: string | null
  summary: string | null
}

// GSA-authored announcement (ag-009) — public surface is published-only.
interface Announcement {
  id: number
  title: string
  body: string
  status: string
  published_at: string | null
}

interface NewsItem {
  id: number
  title: string
  summary: string | null
  published_at: string | null
  category: string | null
  source_url: string | null
  source_name: string | null
}

// Accept #rgb / #rgba / #rrggbb / #rrggbbaa. A missing/blank/invalid value falls
// back to the default theme rather than crashing createTheme's contrast math.
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // meeting_date is date-only ISO; Date() parses it as UTC midnight, so render
  // in UTC too or the day shifts back in western timezones.
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export default function TenantPage() {
  const { slug } = useParams<{ slug: string }>()
  const [tenant, setTenant] = useState<TenantPage | null>(null)
  const [meetings, setMeetings] = useState<Meeting[]>([])
  const [announcements, setAnnouncements] = useState<Announcement[]>([])
  const [news, setNews] = useState<NewsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) {
      setNotFound(true)
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    setNotFound(false)

    fetch(apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}`), { signal: controller.signal })
      .then((res) => {
        // Unknown / inactive slug → 404 (identical body server-side). Any other
        // non-ok is also treated as "not available" → NotFound page.
        if (!res.ok) return Promise.reject(res.status)
        return res.json() as Promise<TenantPage>
      })
      .then((data) => {
        setTenant(data)
        // Content sections are best-effort: a failed meetings/news fetch leaves
        // that section empty rather than 404-ing the whole branded page.
        return Promise.all([
          fetch(apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}/meetings?limit=50`), {
            signal: controller.signal,
          })
            .then((res) => (res.ok ? (res.json() as Promise<Meeting[]>) : []))
            .then((rows) => setMeetings(Array.isArray(rows) ? rows : []))
            .catch(() => setMeetings([])),
          fetch(apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}/announcements?limit=10`), {
            signal: controller.signal,
          })
            .then((res) => (res.ok ? (res.json() as Promise<Announcement[]>) : []))
            .then((rows) => setAnnouncements(Array.isArray(rows) ? rows : []))
            .catch(() => setAnnouncements([])),
          fetch(apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}/news?limit=12`), {
            signal: controller.signal,
          })
            .then((res) => (res.ok ? (res.json() as Promise<NewsItem[]>) : []))
            .then((rows) => setNews(Array.isArray(rows) ? rows : []))
            .catch(() => setNews([])),
        ])
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return
        setNotFound(true)
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [slug])

  // Branded theme: override primary.main only when a valid hex color is present.
  // createTheme(base, overrides) deep-merges and recomputes contrastText.
  const brandedTheme = useMemo(() => {
    const color = tenant?.primary_color?.trim()
    if (!color || !HEX_COLOR_RE.test(color)) return baseTheme
    try {
      // createTheme(base, overrides) deep-merges. The base theme is cast to
      // ThemeOptions because the project augments SimplePaletteColorOptions with
      // an index signature, which a fully-resolved PaletteColor doesn't satisfy.
      return createTheme(baseTheme as unknown as ThemeOptions, {
        palette: { primary: { main: color } },
      })
    } catch {
      // An unexpected color string that slips past the regex must never crash.
      return baseTheme
    }
  }, [tenant?.primary_color])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 16 }}>
        <CircularProgress size={36} sx={{ color: tokens.green.main }} />
      </Box>
    )
  }

  if (notFound || !tenant) {
    return <NotFound />
  }

  const displayName = tenant.display_name?.trim() || tenant.gsa.name
  const { basin } = tenant.gsa

  return (
    <ThemeProvider theme={brandedTheme}>
      {/* ── Branded header ─────────────────────────────────────────────────── */}
      <Box sx={{ background: tokens.green.dark, color: '#fff', py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center' }}>
            {tenant.logo_url ? (
              <Avatar
                src={tenant.logo_url}
                alt={`${displayName} logo`}
                variant="rounded"
                sx={{ width: 64, height: 64, bgcolor: '#fff' }}
              />
            ) : null}
            <Box>
              <Typography variant="overline" sx={{ color: alphaWhite(), display: 'block', mb: 0.5 }}>
                Groundwater Intelligence
              </Typography>
              <Typography variant="h1" sx={{ color: '#fff', fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 700 }}>
                {displayName}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                {tenant.gsa.county ? (
                  <Chip
                    label={`${tenant.gsa.county} County`}
                    size="small"
                    sx={{ bgcolor: alphaWhite(0.12), color: '#fff' }}
                  />
                ) : null}
                {basin?.name ? (
                  <Chip label={`${basin.name} Basin`} size="small" sx={{ bgcolor: alphaWhite(0.12), color: '#fff' }} />
                ) : null}
                {basin?.is_critically_overdrafted ? (
                  <Chip label="Critically overdrafted" size="small" color="warning" />
                ) : null}
              </Stack>
              {/* Quiet staff entry point to the self-serve portal (ag-009) */}
              <Link
                component={RouterLink}
                to={`/g/${tenant.slug}/admin`}
                sx={{ color: alphaWhite(0.55), fontSize: '0.78rem', display: 'inline-block', mt: 1.5 }}
                underline="hover"
              >
                For GSA staff → admin sign-in
              </Link>
            </Box>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        {/* ── Announcements (GSA-authored, publish directly — ag-009) ─────── */}
        {announcements.length > 0 ? (
          <Box sx={{ mb: 8 }}>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Announcements
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Official notices posted by {displayName}.
            </Typography>
            <Stack spacing={2}>
              {announcements.map((a) => (
                <Card key={a.id}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}
                    >
                      <Chip
                        icon={<IconSpeakerphone size={12} />}
                        label={`Posted by ${displayName}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {a.published_at ? (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(a.published_at)}
                        </Typography>
                      ) : null}
                    </Stack>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: a.body ? 1 : 0 }}>
                      {a.title}
                    </Typography>
                    {a.body ? (
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}
                      >
                        {a.body}
                      </Typography>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        ) : null}

        {/* ── Latest meetings ─────────────────────────────────────────────── */}
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          Latest meetings
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Board and committee meetings for {displayName}, most recent first.
        </Typography>

        {meetings.length === 0 ? (
          <EmptyRow label="No meetings recorded yet. Check back after the next board meeting." />
        ) : (
          <Stack spacing={2} sx={{ mb: 5 }}>
            {meetings.slice(0, 3).map((m) => (
              <Card key={m.meeting_id}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                    <IconCalendar size={15} />
                    <Typography variant="caption" sx={{ letterSpacing: '0.03em' }}>
                      {formatDate(m.meeting_date)}
                    </Typography>
                    {m.meeting_type ? (
                      <Chip label={m.meeting_type} size="small" variant="outlined" color="primary" />
                    ) : null}
                  </Stack>
                  {/* Only human-approved summaries render. When summary_title /
                      summary are null there is no approved text — show date +
                      type only; never a raw/extracted decision blob. */}
                  {m.summary_title ? (
                    <Typography variant="h6" sx={{ fontSize: '1.1rem', mb: m.summary ? 1 : 0 }}>
                      {m.summary_title}
                    </Typography>
                  ) : null}
                  {m.summary ? (
                    <>
                      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
                        {m.summary}
                      </Typography>
                      {/* Provenance: this text is ours, and it passed review. */}
                      <Chip
                        label="AgroLense summary · reviewed"
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ mt: 1.5 }}
                      />
                    </>
                  ) : !m.summary_title ? (
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                      Summary pending review.
                    </Typography>
                  ) : null}
                  {/* GSA-posted document links (ag-009) */}
                  {m.agenda_url || m.minutes_url || m.video_url ? (
                    <Stack
                      direction="row"
                      spacing={2}
                      useFlexGap
                      sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}
                    >
                      <DocLink href={m.agenda_url} label="Agenda" />
                      <DocLink href={m.minutes_url} label="Minutes" />
                      <DocLink href={m.video_url} label="Video" />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Posted by {displayName}
                      </Typography>
                    </Stack>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* ── The one Meetings table (Delta-Mendota pattern, ag-008) ───────── */}
        {meetings.length > 0 ? (
          <Box sx={{ mb: 8 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              All meetings
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Agendas, minutes and video links are posted directly by {displayName}.
            </Typography>
            <TableContainer component={Card} sx={{ '&:hover': { transform: 'none' } }}>
              <Table size="small" aria-label="Meetings">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Agenda</TableCell>
                    <TableCell>Minutes</TableCell>
                    <TableCell>Video</TableCell>
                    <TableCell>Summary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetings.map((m) => (
                    <TableRow key={`row-${m.meeting_id}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(m.meeting_date) || '—'}
                      </TableCell>
                      <TableCell>{m.meeting_type ?? '—'}</TableCell>
                      <TableCell>
                        <DocLink href={m.agenda_url} label="View" empty="—" />
                      </TableCell>
                      <TableCell>
                        <DocLink href={m.minutes_url} label="View" empty="—" />
                      </TableCell>
                      <TableCell>
                        <DocLink href={m.video_url} label="Watch" empty="—" />
                      </TableCell>
                      <TableCell>
                        {m.summary_title || m.summary ? (
                          <Chip
                            label="AgroLense summary · reviewed"
                            size="small"
                            variant="outlined"
                            color="secondary"
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : null}

        {/* ── News ─────────────────────────────────────────────────────────── */}
        <Divider sx={{ mb: 5 }} />
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          News
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Published updates relevant to {displayName}.
        </Typography>

        {news.length === 0 ? (
          <EmptyRow label="No published news yet." />
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
              gap: 3,
              mb: 4,
            }}
          >
            {news.map((n) => {
              const inner = (
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<IconNews size={12} />}
                      label={n.category ?? 'Update'}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="h6" sx={{ fontSize: '1.05rem', mb: 1, lineHeight: 1.35 }}>
                    {n.title}
                  </Typography>
                  {n.summary ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                      {n.summary}
                    </Typography>
                  ) : null}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: 'center', color: 'text.secondary', mt: 1 }}
                  >
                    <IconCalendar size={13} />
                    <Typography variant="caption">
                      {formatDate(n.published_at)}
                      {n.source_name ? ` · ${n.source_name}` : ''}
                    </Typography>
                  </Stack>
                </CardContent>
              )
              return n.source_url ? (
                <Card key={n.id}>
                  <Link
                    href={n.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    color="inherit"
                  >
                    {inner}
                  </Link>
                </Card>
              ) : (
                <Card key={n.id}>{inner}</Card>
              )
            })}
          </Box>
        )}
      </Container>

      {/* ── GSA-scoped newsletter signup ───────────────────────────────────── */}
      <NewsletterSignup gsaId={tenant.gsa.gsa_id} gsaName={displayName} />

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <Box sx={{ py: 4, textAlign: 'center', background: tokens.cream }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Powered by{' '}
          <Link component={RouterLink} to="/" color="primary" sx={{ fontWeight: 600 }}>
            AgroLense
            <IconChevronRight size={13} style={{ verticalAlign: 'middle' }} />
          </Link>
        </Typography>
      </Box>
    </ThemeProvider>
  )
}

/**
 * A GSA-posted document link. Renders *empty* (or a dash in table cells) when
 * the URL is missing — never a broken link (ag-008: empty cells render "—").
 */
function DocLink({
  href,
  label,
  empty = null,
}: {
  href: string | null | undefined
  label: string
  empty?: string | null
}) {
  if (!href) return empty ? <>{empty}</> : null
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
      {label}
    </Link>
  )
}

function EmptyRow({ label }: { label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, color: 'text.secondary', mb: 4 }}>
      <IconInbox size={20} />
      <Typography variant="body2">{label}</Typography>
    </Box>
  )
}

// White at a given alpha for header accents (kept local to avoid importing alpha
// for one-off usages, mirroring NewsletterSignup's alphaGreen helper).
function alphaWhite(a = 0.6) {
  return `rgba(255, 255, 255, ${a})`
}
